import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import RestorePageRoundedIcon from "@mui/icons-material/RestorePageRounded";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function formatPublishedAt(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Fecha no disponible";

  try {
    return new Intl.DateTimeFormat("es-AR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return date.toLocaleString("es-AR");
  }
}

function formatSessionAt(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Hace un momento";

  try {
    return new Intl.DateTimeFormat("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  }
}

function authorLabel(author = {}) {
  return String(author.name || author.email || "Sistema").trim() || "Sistema";
}

export default function LandingRevisionsDrawer({
  open,
  revisions = [],
  loading = false,
  error = "",
  sessionHistory = [],
  sessionHistoryTotal = sessionHistory.length,
  canRedo = false,
  restoringSiteRevision = null,
  currentPublishedRevision = 0,
  onClose,
  onReload,
  onUndo,
  onRedo,
  onRestoreSessionCheckpoint,
  onRestore,
}) {
  const compactEditor = useMediaQuery("(max-width:1100px)");
  const mobile = useMediaQuery("(max-width:700px)");
  const latestSiteRevision = Number(revisions[0]?.siteRevision || 0);

  return (
    <Drawer
      anchor={compactEditor ? "bottom" : "right"}
      open={open}
      onClose={restoringSiteRevision ? undefined : onClose}
      PaperProps={{
        sx: {
          width: compactEditor ? "100%" : 460,
          maxWidth: "100%",
          maxHeight: compactEditor ? (mobile ? "88vh" : "74vh") : "100%",
          borderRadius: compactEditor ? "20px 20px 0 0" : 0,
          bgcolor: "#F7F8FA",
          ...(compactEditor ? {
            "& .MuiButton-root": { minHeight: 44 },
            "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
          } : {}),
        },
      }}
    >
      <Stack sx={{ height: compactEditor ? (mobile ? "88vh" : "74vh") : "100%", minHeight: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 2, py: 1.6, bgcolor: "#fff" }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(243,90,58,.10)",
                color: BRAND.orange,
                flex: "0 0 auto",
              }}
            >
              <HistoryRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Historial y recuperación
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                Volvé atrás sin manejar versiones técnicas
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Cerrar revisiones"
            onClick={onClose}
            disabled={Boolean(restoringSiteRevision)}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Los cambios de esta sesión se agrupan por acción para que escribir en un mismo campo
              no genere un paso por cada tecla. Las publicaciones anteriores quedan guardadas aparte
              y recuperarlas sólo reemplaza el borrador: la web pública no cambia hasta que publiques.
            </Alert>

            <Box
              sx={{
                p: 1.7,
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid rgba(25,36,47,.09)",
                boxShadow: "0 8px 24px rgba(25,36,47,.04)",
              }}
            >
              <Stack spacing={1.3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                      Cambios recientes de esta sesión
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)" }}>
                      Se conservan mientras este editor siga abierto.
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`${sessionHistoryTotal} ${sessionHistoryTotal === 1 ? "grupo" : "grupos"}`}
                    sx={{ fontWeight: 850 }}
                  />
                </Stack>

                {sessionHistory.length ? (
                  <Stack spacing={0.9}>
                    {sessionHistory.map((entry, index) => (
                      <Box
                        key={entry.id}
                        sx={{
                          p: 1.15,
                          borderRadius: 2.5,
                          bgcolor: index === 0 ? "rgba(243,90,58,.045)" : "rgba(25,36,47,.025)",
                          border: index === 0
                            ? "1px solid rgba(243,90,58,.16)"
                            : "1px solid rgba(25,36,47,.07)",
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 850, color: BRAND.dark }} noWrap>
                              {entry.label || "Cambio de la página"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)" }}>
                              {index === 0 ? "Último grupo" : "Cambio agrupado"} · {formatSessionAt(entry.recordedAt)}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            disabled={Boolean(restoringSiteRevision)}
                            onClick={() => onRestoreSessionCheckpoint?.(entry.id)}
                            sx={{
                              flexShrink: 0,
                              borderRadius: 999,
                              textTransform: "none",
                              fontWeight: 850,
                              color: BRAND.orange,
                            }}
                          >
                            Volver antes
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
                    Todavía no hiciste cambios recuperables en esta sesión.
                  </Typography>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!sessionHistory.length || Boolean(restoringSiteRevision)}
                    onClick={onUndo}
                    sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                  >
                    Deshacer último
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!canRedo || Boolean(restoringSiteRevision)}
                    onClick={onRedo}
                    sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                  >
                    Rehacer
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 0.4 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.52)" }}>
                PUBLICACIONES ANTERIORES
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Stack>

            {error ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={onReload}>
                    Reintentar
                  </Button>
                }
                sx={{ borderRadius: 3 }}
              >
                {error}
              </Alert>
            ) : null}

            {loading ? (
              <Stack alignItems="center" spacing={1.2} sx={{ py: 6 }}>
                <CircularProgress size={30} sx={{ color: BRAND.orange }} />
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
                  Cargando revisiones…
                </Typography>
              </Stack>
            ) : null}

            {!loading && !error && revisions.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px dashed rgba(25,36,47,.18)",
                  textAlign: "center",
                }}
              >
                <HistoryRoundedIcon sx={{ color: "rgba(25,36,47,.30)", fontSize: 36 }} />
                <Typography sx={{ mt: 1, fontWeight: 900, color: BRAND.dark }}>
                  Todavía no hay publicaciones recuperables
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(25,36,47,.60)" }}>
                  Cada publicación nueva aparecerá acá. Para errores recientes usá el historial de esta sesión.
                </Typography>
              </Box>
            ) : null}

            {!loading && !error
              ? revisions.map((revision) => {
                  const siteRevision = Number(revision.siteRevision || 0);
                  const publishedRevision = Number(revision.publishedRevision || 0);
                  const restoring = restoringSiteRevision === siteRevision;
                  const isCurrent =
                    siteRevision === latestSiteRevision &&
                    publishedRevision === Number(currentPublishedRevision || 0);

                  return (
                    <Box
                      key={`${siteRevision}-${revision.publishedAt || "revision"}`}
                      sx={{
                        p: 1.7,
                        borderRadius: 3,
                        bgcolor: "#fff",
                        border: isCurrent
                          ? "1px solid rgba(46,125,50,.30)"
                          : "1px solid rgba(25,36,47,.09)",
                        boxShadow: "0 8px 24px rgba(25,36,47,.05)",
                      }}
                    >
                      <Stack spacing={1.3}>
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                              {isCurrent ? "Versión publicada actual" : "Versión publicada"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.60)" }}>
                              {formatPublishedAt(revision.publishedAt)}
                            </Typography>
                          </Box>
                          {isCurrent ? (
                            <Chip
                              size="small"
                              color="success"
                              label="Actual"
                              sx={{ fontWeight: 850 }}
                            />
                          ) : null}
                        </Stack>

                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <PersonOutlineRoundedIcon
                            fontSize="small"
                            sx={{ color: "rgba(25,36,47,.45)" }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: BRAND.dark }} noWrap>
                              {authorLabel(revision.author)}
                            </Typography>
                            {revision.author?.role ? (
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)" }} noWrap>
                                {revision.author.role}
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>

                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={
                            restoring ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <RestorePageRoundedIcon />
                            )
                          }
                          disabled={Boolean(restoringSiteRevision)}
                          onClick={() => onRestore?.(revision)}
                          sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 900,
                            color: BRAND.dark,
                            borderColor: "rgba(25,36,47,.18)",
                          }}
                        >
                          {restoring ? "Recuperando borrador…" : "Recuperar como borrador"}
                        </Button>
                      </Stack>
                    </Box>
                  );
                })
              : null}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}
